import React from "react";
import { cn } from "../utils";
import { shell } from "../tokens";

export const MODULE_SIDEBAR_WIDTH = 260;
export const MODULE_SIDEBAR_TRANSITION =
  "width 320ms cubic-bezier(0.4, 0, 0.2, 1), opacity 260ms cubic-bezier(0.4, 0, 0.2, 1)";
export const MODULE_SIDEBAR_PANEL_TRANSITION =
  "transform 320ms cubic-bezier(0.4, 0, 0.2, 1), opacity 260ms cubic-bezier(0.4, 0, 0.2, 1)";

function CollapseIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="9" y1="3" x2="9" y2="21" />
    </svg>
  );
}

function ExpandIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="9" y1="3" x2="9" y2="21" />
      <path d="M13 9l3 3-3 3" />
    </svg>
  );
}

export function ModuleSidebar({ open = true, children, className }) {
  return (
    <aside
      style={{
        width: open ? MODULE_SIDEBAR_WIDTH : 0,
        minWidth: 0,
        overflow: "hidden",
        flexShrink: 0,
        transition: MODULE_SIDEBAR_TRANSITION,
        opacity: open ? 1 : 0,
        pointerEvents: open ? "auto" : "none",
      }}
      className={cn("bg-white border-r border-gray-200 flex flex-col h-full", className)}
      aria-hidden={!open}
    >
      <div
        style={{
          width: MODULE_SIDEBAR_WIDTH,
          transform: open ? "translateX(0)" : "translateX(-20px)",
          opacity: open ? 1 : 0,
          transition: MODULE_SIDEBAR_PANEL_TRANSITION,
        }}
        className="flex flex-col h-full overflow-y-auto"
      >
        {children}
      </div>
    </aside>
  );
}

export function ModuleSidebarHeader({ title, onCollapse, collapseTitle = "Collapse sidebar", actions }) {
  return (
    <div className="flex items-center h-[64px] border-b border-[#E5E7EB] px-4 gap-2 flex-shrink-0">
      <span
        className="truncate flex-1"
        style={shell.moduleSidebar.title}
      >
        {title}
      </span>
      {actions}
      {onCollapse && (
        <button
          type="button"
          onClick={onCollapse}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all duration-200 active:scale-95 flex-shrink-0"
          title={collapseTitle}
          aria-label={collapseTitle}
        >
          <CollapseIcon />
        </button>
      )}
    </div>
  );
}

export function ModuleSidebarNav({ children, className }) {
  return (
    <nav className={cn("flex-1 px-2 py-2 space-y-1", className)}>
      {children}
    </nav>
  );
}

/** Section label inside module sidebars (e.g. "Recents"). */
export function ModuleSidebarSectionLabel({ children, className }) {
  return (
    <div
      className={cn(
        "px-3 pt-4 pb-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide flex-shrink-0",
        className
      )}
    >
      {children}
    </div>
  );
}

/** List row for secondary sidebar content (chat history, etc.). */
export function ModuleSidebarSubItem({ active = false, className, ...props }) {
  return (
    <div
      className={cn(
        "w-full rounded-[10px] transition px-3 py-2 text-left",
        active ? "bg-[#F3F4F6] text-[#2C2C2C] font-medium" : "text-[#4B5563] hover:bg-[#F3F4F6]",
        className
      )}
      style={shell.moduleSidebar.item}
      {...props}
    />
  );
}

function renderSidebarIcon(Icon) {
  if (!Icon) return null;
  if (React.isValidElement(Icon)) return Icon;
  return <Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />;
}

export function ModuleSidebarItem({
  active = false,
  disabled = false,
  onClick,
  icon: Icon,
  children,
  badge,
  badgeVariant = "default",
  title,
  className,
}) {
  const badgeClass =
    badgeVariant === "danger"
      ? "bg-red-500"
      : badgeVariant === "muted"
        ? "bg-gray-400"
        : "bg-blue-600";

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={title}
      className={cn(
        "w-full h-[40px] rounded-[10px] transition-all duration-200 flex items-center px-3 gap-3",
        disabled && "text-gray-400 cursor-default opacity-50",
        !disabled && active && "bg-[#F3F4F6] text-[#2C2C2C] font-medium",
        !disabled && !active && "text-[#4B5563] hover:bg-[#F3F4F6]",
        className
      )}
      style={shell.moduleSidebar.item}
    >
      {renderSidebarIcon(Icon)}
      <span className="flex-1 text-left truncate">{children}</span>
      {badge != null && Number(badge) > 0 && (
        <span
          className={cn(
            "min-w-[20px] h-5 px-1.5 text-white rounded-full flex items-center justify-center flex-shrink-0",
            badgeClass
          )}
          style={{ fontSize: "0.6rem", fontWeight: 700 }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

/** Expand control for module content top bars when the sidebar is collapsed. */
export function ModuleSidebarExpandControl({
  open,
  onExpand,
  expandTitle = "Expand sidebar",
  showDivider = true,
  className,
}) {
  if (open) return null;

  return (
    <>
      <button
        type="button"
        onClick={onExpand}
        className={cn(
          "w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500",
          "hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300",
          "transition-all duration-200 active:scale-95 shadow-sm animate-fade-slide-down flex-shrink-0 self-center",
          className
        )}
        title={expandTitle}
        aria-label={expandTitle}
      >
        <ExpandIcon />
      </button>
      {showDivider && <div className="w-px h-5 bg-gray-200 flex-shrink-0 self-center" />}
    </>
  );
}
